"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultingHero() {
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stats = [{
    label: "15+ Years Experience"
  }, {
    label: "50+ Hotels Optimized"
  }, {
    label: "Revenue Growth Focused"
  }];
  const quickLinks = [{
    text: "Read Related Blog Post",
    url: "/BlogPage"
  }, {
    text: "Explore Other Services",
    url: "/OurServicesPage"
  }, {
    text: "Download Free Checklist",
    url: "/ResourcesPage"
  }, {
    text: "Schedule Consultation",
    url: "/ContactPage"
  }];
  return <section data-section-id="542" id="consultinghero" className="bg-background text-foreground relative w-full overflow-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://hotelrevbaba.com/HomePage"
        }, {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://hotelrevbaba.com/OurServicesPage"
        }, {
          "@type": "ListItem",
          "position": 3,
          "name": "Hotel Tech Stack Consulting",
          "item": "https://hotelrevbaba.com/HotelTechStackConsultingPage"
        }]
      })
    }} />
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {}
        <div ref={imageRef} className={`relative h-[50vh] lg:h-auto overflow-hidden lg:rounded-r-lg transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png-wvc-sizes" alt="Sunil Singh analyzing hotel revenue data on a dashboard" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/60 via-transparent to-transparent pointer-events-none" />
        </div>

        {}
        <div ref={contentRef} className={`relative flex items-center bg-background transition-all duration-1000 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 py-16 lg:py-24 flex flex-col gap-10">
            {}
            <div className="flex items-center gap-3">
              <span className="inline-block h-px w-8 bg-secondary" />
              <span className="font-default text-xs tracking-widest uppercase font-semibold" style={{
              color: "var(--secondary)"
            }}>
                Revenue Management
              </span>
            </div>

            {}
            <h1 className="font-default font-bold text-foreground text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]" style={{
            letterSpacing: "-0.04em"
          }}>
              Maximize Your Hotel’s Revenue with Data-Driven OTA Strategies.
            </h1>

            {}
            <p className="font-default text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed">
              Partner with HotelRevBaBa to unlock hidden potential. We combine deep hospitality tech expertise with hands-on revenue management to drive direct bookings and optimize your OTA performance.
            </p>

            {}
            <h2 className="font-default text-xl font-semibold text-foreground">
              Expert Hospitality Technology & Revenue Solutions
            </h2>

            {}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5">
              <Button asChild className="btn-primary">
                <Link to="/ContactPage" className="flex items-center gap-3">
                  <span className="font-default text-base font-medium">Book a Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="ghost" className="h-auto px-0 py-2 justify-start text-foreground hover:bg-transparent hover:text-secondary group">
                <Link to="/OurServicesPage" className="flex items-center gap-3 border-b border-border hover:border-secondary pb-2 transition-colors">
                  <span className="font-default text-base font-medium">View Our Services</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            {}
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {quickLinks.map((link, i) => <Link key={i} data-index={i} to={link.url} className="font-default text-sm font-medium text-muted-foreground hover:text-secondary transition-colors underline underline-offset-4 decoration-border hover:decoration-secondary">
                  {link.text}
                </Link>)}
            </div>

            {}
            <div className="mt-4 lg:mt-8 self-start lg:self-end">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-default text-xs sm:text-sm text-muted-foreground tabular-nums">
                {stats.map((stat, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                    {i > 0 && <span aria-hidden="true" className="inline-block h-1 w-1 rounded-md bg-secondary" />}
                    <span>{stat.label}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}